import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Edit2, X, Loader2 } from 'lucide-react';
import { notesApi } from '../../lib/api';

const SOAP_FIELDS = [
  { key: 'subjective', label: 'Subjective', description: "Patient's symptoms and history in their own words" },
  { key: 'objective', label: 'Objective', description: 'Vital signs, examination findings, investigations' },
  { key: 'assessment', label: 'Assessment', description: 'Diagnosis or differential diagnoses' },
  { key: 'plan', label: 'Plan', description: 'Treatment, medications, investigations, follow-up' },
];

export default function SOAPNoteEditor({ note }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // which field is being edited
  const [localValues, setLocalValues] = useState({
    subjective: note.subjective || '',
    objective: note.objective || '',
    assessment: note.assessment || '',
    plan: note.plan || '',
  });

  const updateMutation = useMutation({
    mutationFn: (data) => notesApi.update(note.id, data),
    onSuccess: () => {
      qc.invalidateQueries(['consultation-notes']);
      setEditing(null);
    },
  });

  const finaliseMutation = useMutation({
    mutationFn: () => notesApi.finalise(note.id),
    onSuccess: () => qc.invalidateQueries(['consultation-notes']),
  });

  const handleSaveField = (field) => {
    updateMutation.mutate({ [field]: localValues[field] });
  };

  const handleCancelEdit = (field) => {
    setLocalValues(v => ({ ...v, [field]: note[field] || '' }));
    setEditing(null);
  };

  const isFinalised = note.status === 'finalised';

  return (
    <div className="card">
      {/* Note header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-clinical-border">
        <div>
          <h3 className="font-semibold text-slate-800">SOAP Note</h3>
          {note.ai_model && (
            <p className="text-xs text-clinical-muted mt-0.5">
              Generated by AI · {note.generation_time_ms ? `${(note.generation_time_ms / 1000).toFixed(1)}s` : ''}
              {' · '}{note.ai_prompt_tokens + note.ai_output_tokens} tokens
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className={`badge ${isFinalised ? 'badge-green' : 'badge-yellow'}`}>{note.status}</span>
          {!isFinalised && (
            <button
              onClick={() => finaliseMutation.mutate()}
              disabled={finaliseMutation.isPending}
              className="btn-primary text-xs py-1.5 px-3"
            >
              {finaliseMutation.isPending
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Check className="w-3.5 h-3.5" />}
              Finalise Note
            </button>
          )}
        </div>
      </div>

      {/* SOAP fields */}
      <div className="divide-y divide-clinical-border">
        {SOAP_FIELDS.map(({ key, label, description }) => (
          <div key={key} className="px-5 py-4">
            <div className="flex items-start justify-between mb-2">
              <div>
                <span className="text-xs font-bold text-primary-600 uppercase tracking-wider">{label}</span>
                <p className="text-xs text-clinical-muted">{description}</p>
              </div>
              {!isFinalised && editing !== key && (
                <button
                  onClick={() => setEditing(key)}
                  className="btn-ghost py-1 px-2 text-xs"
                >
                  <Edit2 className="w-3 h-3" />
                  Edit
                </button>
              )}
            </div>

            {editing === key ? (
              <div>
                <textarea
                  className="w-full input min-h-[120px] resize-y font-mono text-sm"
                  value={localValues[key]}
                  onChange={(e) => setLocalValues(v => ({ ...v, [key]: e.target.value }))}
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => handleSaveField(key)}
                    disabled={updateMutation.isPending}
                    className="btn-primary text-xs py-1.5 px-3"
                  >
                    {updateMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </button>
                  <button
                    onClick={() => handleCancelEdit(key)}
                    className="btn-ghost text-xs py-1.5 px-3"
                  >
                    <X className="w-3 h-3" />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">
                {note[key] || <span className="text-clinical-muted italic">Not documented</span>}
              </p>
            )}
          </div>
        ))}

        {/* Prescriptions */}
        {note.content?.prescriptions?.length > 0 && (
          <div className="px-5 py-4">
            <span className="text-xs font-bold text-primary-600 uppercase tracking-wider">Prescriptions</span>
            <div className="mt-2 space-y-1">
              {note.content.prescriptions.map((rx, i) => (
                <div key={i} className="text-sm text-slate-800 flex gap-2">
                  <span className="font-medium">{rx.medication}</span>
                  {rx.dose && <span className="text-clinical-muted">{rx.dose}</span>}
                  {rx.frequency && <span className="text-clinical-muted">{rx.frequency}</span>}
                  {rx.duration && <span className="text-clinical-muted">for {rx.duration}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ICD codes */}
        {note.content?.icdCodes?.length > 0 && (
          <div className="px-5 py-4">
            <span className="text-xs font-bold text-primary-600 uppercase tracking-wider">Suggested ICD-10 Codes</span>
            <div className="mt-2 flex flex-wrap gap-1">
              {note.content.icdCodes.map((code, i) => (
                <span key={i} className="badge-gray font-mono">{code}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
